import React from 'react';
import { ActionDropDown, Badge, Dropdown } from '@doveit/bricks';
import { Feedback } from '../../../providers/api/dtos';
import { feedbackStatusColors, feedbackStatusLabels } from '../../constants';
import { FeedbackStatus } from '../../../domain/types';
import StartSurveyAction from '../start-survey-action/StartSurveyAction';
import { ViewFeedbackAction } from '../view-feedback-action/ViewFeedbackAction';
import ArchiveFeedbackAction from '../archive-feedback-action/ArchiveFeedbackAction';
import RestoreFeedbackAction from '../restore-feedback-action/RestoreFeedbackAction';

export interface UpdateFeedbackStatusProps extends React.AriaAttributes {
  feedback: Feedback,
  showAsBadge?: boolean,
  emphasis?: 'low' | 'high',
  size?: 'XS' | 'S',
  onSuccess?: (feedback: Feedback) => void,
}

const UpdateFeedbackStatus: React.FC<UpdateFeedbackStatusProps> = ({
  feedback,
  showAsBadge,
  emphasis,
  size,
  onSuccess,
  ...rest
}) => {
  if ([FeedbackStatus.COMPLETED, FeedbackStatus.REVIEWED].includes(feedback.status) || showAsBadge) {
    return (
      <Badge
        aria-label="Stato della survey"
        label={feedbackStatusLabels[feedback.status]}
        color={feedbackStatusColors[feedback.status]}
        emphasis={emphasis}
        size={size}
        {...rest}
      />
    );
  }

  return (
    <ActionDropDown
      aria-label="Seleziona un nuovo stato della survey"
      label={feedbackStatusLabels[feedback.status]}
      color={feedbackStatusColors[feedback.status]}
      emphasis={emphasis}
      size={size}
      {...rest}
    >
      {feedback.status === FeedbackStatus.TODO && (
        <>
          <StartSurveyAction
            feedback={feedback}
            onSubmit={() => onSuccess?.(feedback)}
          >
            {({ startSurvey }) => (
              <Dropdown.Option
                label="Compila"
                aria-label="Compila survey"
                onClick={startSurvey}
              />
            )}
          </StartSurveyAction>
          <ArchiveFeedbackAction
            feedback={feedback}
            onSuccess={onSuccess}
          >
            {({ archiveFeedback, isLoading }) => (
              <Dropdown.Option
                label="Archivia"
                aria-label="Archivia survey"
                onClick={archiveFeedback}
                loading={isLoading}
              />
            )}
          </ArchiveFeedbackAction>
        </>
      )}
      {feedback.status === FeedbackStatus.TO_REVIEW && (
        <ViewFeedbackAction
          feedback={feedback}
          newStatus={FeedbackStatus.REVIEWED}
          canEdit
          onSuccess={onSuccess}
        >
          {({ view }) => (
            <Dropdown.Option
              label="Revisiona"
              aria-label="Revisiona survey"
              onClick={view}
            />
          )}
        </ViewFeedbackAction>
      )}
      {[FeedbackStatus.ARCHIVED, FeedbackStatus.KO_CANNOT_CONTACT].includes(feedback.status) && (
        <RestoreFeedbackAction
          feedback={feedback}
          onSuccess={onSuccess}
        >
          {({ restoreFeedback, isLoading }) => (
            <Dropdown.Option
              label="Ripristina"
              aria-label="Ripristina survey"
              loading={isLoading}
              onClick={restoreFeedback}
            />
          )}
        </RestoreFeedbackAction>
      )}
    </ActionDropDown>
  );
};

export default UpdateFeedbackStatus;
